import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useOutletContext, Link } from "react-router-dom";
import {
  SearchAndSort,
  SortOption,
  SkeletonCard,
  EmptyState,
  Pagination,
} from "../../features/statsNew/components";
import type { DateRange } from "../../features/statsNew/components";
import { ScoreProgressionChart, SessionScoreData } from "../../features/statsNew/components/charts";
import { getSessionDate } from "../../features/statsNew/utils/dateUtils";
import { setDateRange } from "../../features/statsNew/state/statsNewSlice";
import {
  getAllSessionsByProjectGroup,
  sessionsByProjectGroupSelector,
} from "../../features/stats/state/statsSlice";
import {
  fetchProjectGroupsWithProjects,
  getAllProjectGroupsWithNestedElements,
} from "../../features/projectGroups/state/projectGroupsSlice";
import { AppDispatch } from "../../common/state/store";
import { STATS_FETCH_ALL_LIMIT } from "../../features/statsNew/constants";

interface OutletContextType {
  dateRange: DateRange;
}

interface UserInfo {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  username: string;
  avatarUrl?: string;
}

const getSessionBadgeColor = (sessionCount: number): string => {
  if (sessionCount <= 5) return "bg-gray-400";
  if (sessionCount <= 50) return "bg-green-500";
  return "bg-green-600";
};

const ApprenantDetailView = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string }>();
  const { dateRange } = useOutletContext<OutletContextType>();

  // Check if viewing anonymous sessions
  const isAnonymousView = userId === "anonymous";

  const sessionsByProjectGroup = useSelector(sessionsByProjectGroupSelector);
  const projectGroups = useSelector(getAllProjectGroupsWithNestedElements);

  const [searchValue, setSearchValue] = useState("");
  const [sortKey, setSortKey] = useState("activity-desc");
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [showDeleted, setShowDeleted] = useState(false);
  const [chartFilterParcours, setChartFilterParcours] = useState<number | null>(null);
  const [chartFilterModule, setChartFilterModule] = useState<number | null>(null);
  const [chartShowDeleted, setChartShowDeleted] = useState(false);

  useEffect(() => {
    dispatch(getAllSessionsByProjectGroup({ componentId: "statsNewApprenantDetailView" }));
    dispatch(
      fetchProjectGroupsWithProjects({
        componentId: "statsNewApprenantDetailView",
        limit: STATS_FETCH_ALL_LIMIT,
      }),
    );
  }, [dispatch]);

  // Track when data has loaded at least once
  useEffect(() => {
    if (sessionsByProjectGroup.length > 0 && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [sessionsByProjectGroup, isInitialLoad]);

  const handleClearDateRange = () => {
    dispatch(setDateRange({ startDate: null, endDate: null }));
  };

  const sortOptions: SortOption[] = [
    // Par activité
    { key: "activity-desc", label: t("statsNew.sort.activityRecent") },
    { key: "activity-asc", label: t("statsNew.sort.activityOldest") },
    { key: "sessions-desc", label: t("statsNew.sort.sessionsMost") },
    { key: "sessions-asc", label: t("statsNew.sort.sessionsLeast") },
    // Par date
    { key: "firstSession-desc", label: t("statsNew.sort.firstSessionRecent") },
    { key: "firstSession-asc", label: t("statsNew.sort.firstSessionOldest") },
    { key: "created-desc", label: t("statsNew.sort.createdRecent") },
    { key: "created-asc", label: t("statsNew.sort.createdOldest") },
    // Par nom
    { key: "name-asc", label: t("statsNew.sort.nameAZ") },
    { key: "name-desc", label: t("statsNew.sort.nameZA") },
  ];

  // Get user info and filter sessions
  const { userInfo, parcoursData, totalAnonymousSessions } = useMemo(() => {
    let userInfo: UserInfo | null = null;
    let totalAnonymousSessions = 0;
    const parcoursMap = new Map<
      number,
      {
        id: number;
        name: string;
        sessionCount: number;
        lastSessionDate: Date | null;
        firstSessionDate: Date | null;
        createdAt: Date | null;
        moduleCount: number;
        thumbnailUrl: string | null;
        isDeleted: boolean;
      }
    >();

    sessionsByProjectGroup.forEach((group) => {
      let groupSessionCount = 0;
      let groupLastDate: Date | null = null;
      let groupFirstDate: Date | null = null;

      group.projects.forEach((project) => {
        project.sessions.forEach((session) => {
          // Filter by user or anonymous
          if (isAnonymousView) {
            // For anonymous view, only include sessions without enduser
            if (session.enduser) return;
          } else {
            // For specific user view, filter by user ID
            if (session.enduser?.id !== Number(userId)) return;
          }

          const sessionDate = getSessionDate(session);

          // Apply date range filter
          if (dateRange.startDate && sessionDate < dateRange.startDate) return;
          if (dateRange.endDate && sessionDate > dateRange.endDate) return;

          // Capture user info from first matching session (only for non-anonymous)
          if (!isAnonymousView && !userInfo && session.enduser) {
            userInfo = {
              id: session.enduser.id,
              firstName: session.enduser.firstname || "",
              lastName: session.enduser.lastname || "",
              email: session.enduser.email || "",
              username: session.enduser.username || "",
              avatarUrl: session.enduser.source_avatar?.s3_url,
            };
          }

          groupSessionCount++;
          if (isAnonymousView) {
            totalAnonymousSessions++;
          }
          if (!groupLastDate || sessionDate > groupLastDate) {
            groupLastDate = sessionDate;
          }
          if (!groupFirstDate || sessionDate < groupFirstDate) {
            groupFirstDate = sessionDate;
          }
        });
      });

      if (groupSessionCount > 0) {
        // Find thumbnail and createdAt from projectGroups
        const pgData = projectGroups.find((pg) => pg.id === group.project_group_id);
        const thumbnailUrl = pgData?.source_thumbnail?.s3_url || null;
        const createdAt = pgData?.createdAt ? new Date(pgData.createdAt) : null;

        parcoursMap.set(group.project_group_id, {
          id: group.project_group_id,
          name: group.project_group_name,
          sessionCount: groupSessionCount,
          lastSessionDate: groupLastDate,
          firstSessionDate: groupFirstDate,
          createdAt,
          moduleCount: group.projects.length,
          thumbnailUrl,
          isDeleted: group.is_deleted || false,
        });
      }
    });

    return {
      userInfo,
      parcoursData: Array.from(parcoursMap.values()),
      totalAnonymousSessions,
    };
  }, [sessionsByProjectGroup, userId, dateRange, projectGroups, isAnonymousView]);

  // Collect all sessions with scores for the progression chart (with parcours/module info)
  const { allSessionScoreData, totalUserSessions } = useMemo(() => {
    const sessions: (SessionScoreData & {
      parcoursId: number;
      parcoursName: string;
      parcoursIsDeleted: boolean;
      moduleId: number;
      moduleIsDeleted: boolean;
    })[] = [];
    let totalCount = 0;

    sessionsByProjectGroup.forEach((group) => {
      group.projects.forEach((project) => {
        project.sessions.forEach((session) => {
          // Filter by user or anonymous
          if (isAnonymousView) {
            if (session.enduser) return;
          } else {
            if (session.enduser?.id !== Number(userId)) return;
          }

          const sessionDate = getSessionDate(session);

          // Apply date range filter
          if (dateRange.startDate && sessionDate < dateRange.startDate) return;
          if (dateRange.endDate && sessionDate > dateRange.endDate) return;

          totalCount++;

          // Only include sessions with scores in the chart data
          if (session.score !== null && session.score !== undefined) {
            sessions.push({
              id: session.id,
              date: sessionDate,
              score: session.score,
              scoreMax: session.score_max || 20,
              moduleName: project.project_name,
              parcoursId: group.project_group_id,
              parcoursName: group.project_group_name,
              parcoursIsDeleted: group.is_deleted || false,
              moduleId: project.project_id,
              moduleIsDeleted: project.is_deleted || false,
            });
          }
        });
      });
    });

    return { allSessionScoreData: sessions, totalUserSessions: totalCount };
  }, [sessionsByProjectGroup, userId, dateRange, isAnonymousView]);

  // Extract unique parcours and modules for chart filters
  const chartFilterOptions = useMemo(() => {
    const parcoursMap = new Map<number, { name: string; isDeleted: boolean }>();
    const modulesMap = new Map<number, { name: string; parcoursId: number; isDeleted: boolean }>();

    allSessionScoreData.forEach((s) => {
      parcoursMap.set(s.parcoursId, { name: s.parcoursName, isDeleted: s.parcoursIsDeleted });
      modulesMap.set(s.moduleId, {
        name: s.moduleName || "",
        parcoursId: s.parcoursId,
        isDeleted: s.moduleIsDeleted,
      });
    });

    const parcoursList = Array.from(parcoursMap.entries()).map(([id, { name, isDeleted }]) => ({
      id,
      name,
      isDeleted,
    }));
    const modulesList = Array.from(modulesMap.entries()).map(
      ([id, { name, parcoursId, isDeleted }]) => ({
        id,
        name,
        parcoursId,
        isDeleted,
      }),
    );

    return { parcoursList, modulesList };
  }, [allSessionScoreData]);

  // Filter sessions for chart based on selected parcours/module
  const sessionScoreData = useMemo((): SessionScoreData[] => {
    let filtered = allSessionScoreData;

    if (chartFilterParcours) {
      filtered = filtered.filter((s) => s.parcoursId === chartFilterParcours);
    }

    if (chartFilterModule) {
      filtered = filtered.filter((s) => s.moduleId === chartFilterModule);
    }

    return filtered;
  }, [allSessionScoreData, chartFilterParcours, chartFilterModule]);

  // Reset module filter when parcours changes
  useEffect(() => {
    if (chartFilterParcours) {
      // Check if current module belongs to selected parcours
      const moduleInParcours = chartFilterOptions.modulesList.find(
        (m) => m.id === chartFilterModule && m.parcoursId === chartFilterParcours,
      );
      if (!moduleInParcours) {
        setChartFilterModule(null);
      }
    }
  }, [chartFilterParcours, chartFilterModule, chartFilterOptions.modulesList]);

  // Reset filters if selected item is deleted and chartShowDeleted is turned off
  useEffect(() => {
    if (!chartShowDeleted) {
      // Check if selected parcours is deleted
      if (chartFilterParcours) {
        const selectedParcours = chartFilterOptions.parcoursList.find(
          (p) => p.id === chartFilterParcours,
        );
        if (selectedParcours?.isDeleted) {
          setChartFilterParcours(null);
          setChartFilterModule(null);
        }
      }
      // Check if selected module is deleted
      if (chartFilterModule) {
        const selectedModule = chartFilterOptions.modulesList.find(
          (m) => m.id === chartFilterModule,
        );
        if (selectedModule?.isDeleted) {
          setChartFilterModule(null);
        }
      }
    }
  }, [chartShowDeleted, chartFilterParcours, chartFilterModule, chartFilterOptions]);

  const filteredAndSortedData = useMemo(() => {
    let result = parcoursData;

    // Filter out deleted parcours if showDeleted is false
    if (!showDeleted) {
      result = result.filter((p) => !p.isDeleted);
    }

    // Filter by search
    if (searchValue) {
      const search = searchValue.toLowerCase();
      result = result.filter((p) => p.name.toLowerCase().includes(search));
    }

    // Sort
    result = [...result].sort((a, b) => {
      switch (sortKey) {
        case "sessions-desc":
          return b.sessionCount - a.sessionCount;
        case "sessions-asc":
          return a.sessionCount - b.sessionCount;
        case "name-asc":
          return a.name.localeCompare(b.name);
        case "name-desc":
          return b.name.localeCompare(a.name);
        case "activity-desc":
          if (!a.lastSessionDate) return 1;
          if (!b.lastSessionDate) return -1;
          return b.lastSessionDate.getTime() - a.lastSessionDate.getTime();
        case "activity-asc":
          if (!a.lastSessionDate) return 1;
          if (!b.lastSessionDate) return -1;
          return a.lastSessionDate.getTime() - b.lastSessionDate.getTime();
        case "firstSession-desc":
          if (!a.firstSessionDate) return 1;
          if (!b.firstSessionDate) return -1;
          return b.firstSessionDate.getTime() - a.firstSessionDate.getTime();
        case "firstSession-asc":
          if (!a.firstSessionDate) return 1;
          if (!b.firstSessionDate) return -1;
          return a.firstSessionDate.getTime() - b.firstSessionDate.getTime();
        case "created-desc":
          if (!a.createdAt) return 1;
          if (!b.createdAt) return -1;
          return b.createdAt.getTime() - a.createdAt.getTime();
        case "created-asc":
          if (!a.createdAt) return 1;
          if (!b.createdAt) return -1;
          return a.createdAt.getTime() - b.createdAt.getTime();
        default:
          return 0;
      }
    });

    return result;
  }, [parcoursData, searchValue, sortKey, showDeleted]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(0);
  }, [searchValue, sortKey, dateRange, showDeleted]);

  // Paginate the data
  const paginatedData = useMemo(() => {
    const start = currentPage * pageSize;
    return filteredAndSortedData.slice(start, start + pageSize);
  }, [filteredAndSortedData, currentPage, pageSize]);

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(0);
  };

  const handleParcoursClick = (parcoursId: number) => {
    navigate(`/statistics/apprenants/${userId}/${parcoursId}`);
  };

  // Show skeleton during initial load
  if (isInitialLoad) {
    return (
      <div className="space-y-6">
        {/* Breadcrumb skeleton */}
        <div className="flex items-center gap-2">
          <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
          <div className="h-4 w-4 bg-gray-200 rounded animate-pulse" />
          <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
        </div>

        {/* User header skeleton */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-gray-200 animate-pulse" />
            <div>
              <div className="h-6 w-40 bg-gray-200 rounded animate-pulse mb-2" />
              <div className="h-4 w-48 bg-gray-200 rounded animate-pulse" />
            </div>
          </div>
        </div>

        {/* Search skeleton */}
        <div className="h-10 w-64 bg-gray-200 rounded-lg animate-pulse" />

        {/* Cards skeleton */}
        <div className="flex flex-wrap gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <SkeletonCard key={i} variant="parcours" />
          ))}
        </div>
      </div>
    );
  }

  // For non-anonymous view, check if user was found
  if (!isAnonymousView && !userInfo) {
    return (
      <EmptyState
        icon="users"
        title={t("statsNew.apprenants.notFound")}
        description={t("statsNew.emptyState.noApprenantsDescription")}
      />
    );
  }

  // For anonymous view, check if there are any anonymous sessions
  if (isAnonymousView && parcoursData.length === 0 && !isInitialLoad) {
    return (
      <EmptyState
        icon="users"
        title={t("statsNew.apprenants.noAnonymousSessions")}
        description={t("statsNew.emptyState.noApprenantsDescription")}
      />
    );
  }

  const displayName = isAnonymousView
    ? t("statsNew.apprenants.anonymous")
    : userInfo?.firstName || userInfo?.lastName
    ? `${userInfo?.firstName} ${userInfo?.lastName}`.trim()
    : userInfo?.username || "";

  // Render empty state for parcours based on context
  const renderEmptyState = () => {
    if (searchValue) {
      return (
        <EmptyState
          icon="search"
          title={t("statsNew.emptyState.noSearchResults")}
          description={t("statsNew.emptyState.noSearchResultsDescription")}
        />
      );
    }

    if (dateRange.startDate || dateRange.endDate) {
      return (
        <EmptyState
          icon="calendar"
          title={t("statsNew.apprenants.noParcours")}
          description={t("statsNew.emptyState.noParcoursDescription")}
          action={{
            label: t("statsNew.emptyState.viewAll"),
            onClick: handleClearDateRange,
          }}
        />
      );
    }

    return (
      <EmptyState
        icon="folder"
        title={t("statsNew.apprenants.noParcours")}
        description={t("statsNew.emptyState.noParcoursDescription")}
      />
    );
  };

  return (
    <div className="flex flex-col h-full">
      {/* Breadcrumb */}
      <nav className="stats-breadcrumb mb-2">
        <Link to="/statistics/apprenants">{t("statsNew.tabs.apprenants")}</Link>
        <span className="stats-breadcrumb-separator">/</span>
        <span className="stats-breadcrumb-current">{displayName}</span>
      </nav>

      {/* User header */}
      <div className="flex-shrink-0 bg-white rounded-lg border border-gray-200 px-4 py-2 mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
            {isAnonymousView ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            ) : userInfo?.avatarUrl ? (
              <img src={userInfo.avatarUrl} alt="" className="w-full h-full object-cover" />
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
            )}
          </div>
          <div>
            <h2 className="text-base font-bold text-gray-900">{displayName}</h2>
            {isAnonymousView ? (
              <p className="text-xs text-gray-500">
                {totalAnonymousSessions} {t("statsNew.session.sessions")}
              </p>
            ) : (
              <p className="text-xs text-gray-500">
                {totalUserSessions} {t("statsNew.session.sessions")}
                {userInfo?.email && <span className="mx-1">•</span>}
                {userInfo?.email}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Score Progression Chart */}
      {allSessionScoreData.length > 1 && (
        <div className="flex-shrink-0 bg-white rounded-lg border border-gray-200 px-4 py-2 mb-3">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-xs font-semibold text-gray-700">
              {t("statsNew.progression.title")}
            </h3>
            {/* Chart filters */}
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-1 text-xs text-gray-500 cursor-pointer">
                <input
                  type="checkbox"
                  checked={chartShowDeleted}
                  onChange={(e) => setChartShowDeleted(e.target.checked)}
                  className="w-3 h-3 text-green-600 rounded border-gray-300 focus:ring-green-500"
                />
                {t("statsNew.filters.showDeleted")}
              </label>
              <select
                value={chartFilterParcours || ""}
                onChange={(e) => {
                  const val = e.target.value ? Number(e.target.value) : null;
                  setChartFilterParcours(val);
                  if (!val) setChartFilterModule(null);
                }}
                className="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-green-500"
              >
                <option value="">{t("statsNew.progression.allParcours")}</option>
                {chartFilterOptions.parcoursList
                  .filter((p) => chartShowDeleted || !p.isDeleted)
                  .map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                      {p.isDeleted ? ` [${t("statsNew.parcours.deleted")}]` : ""}
                    </option>
                  ))}
              </select>
              {chartFilterParcours && (
                <select
                  value={chartFilterModule || ""}
                  onChange={(e) =>
                    setChartFilterModule(e.target.value ? Number(e.target.value) : null)
                  }
                  className="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-green-500"
                >
                  <option value="">{t("statsNew.progression.allModules")}</option>
                  {chartFilterOptions.modulesList
                    .filter(
                      (m) =>
                        m.parcoursId === chartFilterParcours && (chartShowDeleted || !m.isDeleted),
                    )
                    .map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                        {m.isDeleted ? ` [${t("statsNew.module.deleted")}]` : ""}
                      </option>
                    ))}
                </select>
              )}
            </div>
          </div>
          <div className="h-44">
            {sessionScoreData.length > 1 ? (
              <ScoreProgressionChart
                sessions={sessionScoreData}
                totalSessionsCount={
                  !chartFilterParcours && !chartFilterModule ? totalUserSessions : undefined
                }
              />
            ) : (
              <div className="flex items-center justify-center h-full text-sm text-gray-500">
                {t("statsNew.progression.notEnoughData")}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Search, Sort and Filters */}
      <div className="flex-shrink-0 mb-3 flex items-center gap-4">
        <SearchAndSort
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          searchPlaceholder={t("statsNew.parcours.searchPlaceholder")}
          sortOptions={sortOptions}
          selectedSort={sortKey}
          onSortChange={setSortKey}
        />
        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer whitespace-nowrap">
          <input
            type="checkbox"
            checked={showDeleted}
            onChange={(e) => setShowDeleted(e.target.checked)}
            className="w-4 h-4 text-green-600 rounded border-gray-300 focus:ring-green-500"
          />
          {t("statsNew.filters.showDeleted")}
        </label>
      </div>

      {/* Parcours Grid */}
      {filteredAndSortedData.length === 0 ? (
        renderEmptyState()
      ) : (
        <>
          <div className="flex-1 overflow-y-auto min-h-0">
            <div className="flex flex-wrap gap-3">
              {paginatedData.map((parcours) => (
                <div
                  key={parcours.id}
                  onClick={() => handleParcoursClick(parcours.id)}
                  className="relative group cursor-pointer rounded-lg overflow-hidden shadow hover:shadow-lg transition-all hover:scale-[1.02]"
                  style={{ width: "160px", height: "100px" }}
                >
                  {/* Background image or gradient */}
                  {parcours.thumbnailUrl ? (
                    <img
                      src={parcours.thumbnailUrl}
                      alt={parcours.name}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-slate-600 via-slate-700 to-slate-800">
                      {/* Subtle decorative pattern */}
                      <div className="absolute inset-0 opacity-10">
                        <div className="absolute top-1 right-6 w-10 h-10 border-2 border-white rounded-full" />
                        <div className="absolute bottom-6 left-2 w-5 h-5 border-2 border-white rounded-full" />
                      </div>
                    </div>
                  )}

                  {/* Dark overlay for text readability */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

                  {/* Content */}
                  <div className="absolute inset-0 p-2 flex flex-col justify-end">
                    <h3
                      className="text-white font-semibold text-xs leading-tight line-clamp-2"
                      title={parcours.name}
                    >
                      {parcours.name}
                    </h3>
                  </div>

                  {/* Session count badge */}
                  <div className="absolute top-1.5 right-1.5 transition-transform">
                    <span
                      className={`inline-flex items-center justify-center min-w-[1.5rem] h-5 px-1 ${getSessionBadgeColor(
                        parcours.sessionCount,
                      )} text-white text-[10px] leading-none font-bold rounded-md shadow`}
                    >
                      {parcours.sessionCount} {t("statsNew.session.sessions")}
                    </span>
                  </div>

                  {/* Deleted indicator */}
                  {parcours.isDeleted && (
                    <div className="absolute top-1.5 left-1.5">
                      <span className="inline-flex items-center px-1 py-0.5 bg-red-500 text-white text-[9px] font-medium rounded">
                        {t("statsNew.parcours.deleted")}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Pagination */}
          <div className="flex-shrink-0 mt-auto">
            <Pagination
              currentPage={currentPage}
              totalItems={filteredAndSortedData.length}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
              onPageSizeChange={handlePageSizeChange}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default ApprenantDetailView;
